import { useEffect, useState } from "react";

import { getAggregateIssuances } from "../../service/query";
import { Status } from "../../service/Shared";

export interface UnitsChartData {
  [key: string]: number;
}

const EMPTY: UnitsChartData = { Available: 0, Reserved: 0, Sold: 0 };

/**
 * The Available / Reserved / Sold split for one project, covering only the given issuance statuses.
 *
 * The project's `cached{Available,Reserved,Sold}Quantity` columns cannot serve a status-aware card —
 * they are status-blind totals. `aspects` on `aggregateIssuances` sums the three quantities over
 * exactly the rows the filters select, which is the split these pie charts want.
 *
 * Shared by the Developer/Development Manager and Asset Manager project cards so the two cannot
 * drift apart. Takes the statuses as an argument rather than reading `ProjectContext`, so it stays
 * usable outside that provider.
 */
export const useProjectUnitsChartData = (
  projectUuid: string | undefined,
  selectedStatuses: string[],
  onError?: () => void
): UnitsChartData => {
  const [unitsChartData, setUnitsChartData] = useState<UnitsChartData>(EMPTY);

  useEffect(() => {
    let isCurrent = true;

    /*
      An empty selection is legal state, but an absent `issuanceStatus` filter means "every status"
      server-side, and the server rejects an empty list outright. So no request at all: nothing is
      selected, so nothing is counted.
    */
    if (projectUuid && selectedStatuses.length === 0) {
      setUnitsChartData(EMPTY);
    } else if (projectUuid) {
      getAggregateIssuances({
        filters: [
          { filterBy: "project", filterOperator: "in", filterValue: `[${projectUuid}]` },
          { filterBy: "issuanceStatus", filterOperator: "in", filterValue: `[${selectedStatuses.join(",")}]` },
        ],
        groupBy: ["unitMeasurement"],
        isReadableProject: true,
        paging: { beforeCursor: null, afterCursor: null, limit: 100 },
        sort: [],
        filter: { results: {} },
      })
        .then((response) => {
          if (!isCurrent) {
            return;
          }
          if (response.status !== Status.Success || !response.data) {
            /*
              The chips update the moment the user clicks, so leaving the previous selection's
              totals on screen would label them as the new selection indefinitely. Clear them.
            */
            setUnitsChartData(EMPTY);
            onError?.();
            return;
          }
          setUnitsChartData({
            Available: response.data.aspects.totalAvailableUnits ?? 0,
            Reserved: response.data.aspects.totalReservedUnits ?? 0,
            Sold: response.data.aspects.totalSoldUnits ?? 0,
          });
        })
        .catch(() => {
          if (isCurrent) {
            setUnitsChartData(EMPTY);
            onError?.();
          }
        });
    }

    return () => {
      // A selection changed mid-flight must not be overwritten by the previous request.
      isCurrent = false;
    };
    // `onError` is a caller-supplied toast; re-running the fetch when its identity changes is noise.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectUuid, selectedStatuses]);

  return unitsChartData;
};
